import { checkDefinition, isChoice, jumpsOf, loopSource, operatorLabel, questionTypeLabel, type Condition, type Definition, type JumpIf, type Operator, type Question, type Randomizer } from '@survey/core';
import { AlertTriangle, Box, ChevronsDownUp, ChevronsUpDown, CornerDownRight, ExternalLink, Eye, Flag, GitBranch, GripVertical, ListPlus, Minus, OctagonX, Plus, Repeat, Shuffle, Trash2, Boxes, ChevronDown, ChevronRight, MoreHorizontal, RotateCcw, Info } from 'lucide-react';
import { useEffect, useMemo, useState, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { useI18n } from '../../i18n';
import { api } from '../../api/client';
import { confirmAction, Field, Menu, Modal } from '../ui';
import { conditionPhrase } from './logicText';
import { defaultValue, operatorsFor, ValueInput } from './LogicEditor';
import {
  absorbSection,
  addBranch,
  addEnding,
  branchesOf,
  removeBranch,
  type Branch,
  addRandomizer,
  addSectionToRandomizer,
  deleteSection,
  duplicateSection,
  flowElements,
  logicFrom,
  moveRandomizer,
  moveQuestionTo,
  moveQuestionToSection,
  moveRandomizerTo,
  moveSection,
  moveSectionIntoRandomizer,
  setRandomizerLimits,
  moveSectionTo,
  neighbourSection,
  newSection,
  releaseSection,
  removeRandomizer,
  renameSectionTitle,
  setRandomizerCount,
  startSection,
  TYPE_ICONS,
  type FlowSection,
} from './model';

interface Props {
  def: Definition;
  canEdit: boolean;
  selectedId?: string | null;
  onChange: (fn: (d: Definition) => Definition) => void;
  onSelect: (questionId: string) => void;
  /** Where the flow opens as its own page; shows an "open in new tab" link. */
  openUrl?: string;
  /** The survey, for showing how many respondents each randomizer section has been given. */
  surveyId?: string;
}

type Limiting = { randomizerId: string };
type AddAt = { where: 'below' | 'end'; after?: string } | { where: 'randomizer'; randomizerId: string };
type Naming = { kind: 'new'; after?: string } | { kind: 'rename'; title: string } | { kind: 'in-randomizer'; randomizerId: string } | { kind: 'name-untitled'; firstId: string };

type Drag = { kind: 'section'; title: string } | { kind: 'randomizer'; id: string } | { kind: 'question'; id: string };

const blockKey = (s: FlowSection) => s.title || `q:${s.questions[0]?.id}`;

/**
 * The survey flow as an editor: sections as blocks, randomizers grouping
 * adjacent sections, and the logic of every question visible on both the
 * question that reads an answer and the one it affects. Every action is a pure
 * function from model.ts.
 */
export function FlowEditor({ def, canEdit, selectedId, onChange, onSelect, openUrl, surveyId }: Props) {
  const { t, locale } = useI18n();
  const els = useMemo(() => flowElements(def), [def]);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Problems that only exist because of the randomizers (the checker reports them without naming a question).
  const rzIssues = useMemo(() => {
    if (!def.settings.randomizers?.length) return [];
    const without = new Set(checkDefinition({ ...def, settings: { ...def.settings, randomizers: undefined } }, locale).map((i) => i.message));
    return checkDefinition(def, locale).filter((i) => !without.has(i.message));
  }, [def, locale]);
  const [naming, setNaming] = useState<Naming | null>(null);
  const [limiting, setLimiting] = useState<Limiting | null>(null);
  // Where the "What do you want to add?" panel is open: under a section or randomizer, inside a randomizer, or at the end.
  const [adding, setAdding] = useState<AddAt | null>(null);
  const [branching, setBranching] = useState<{ after?: string; end?: boolean } | null>(null);
  const number = useMemo(() => new Map(def.questions.map((q, i) => [q.id, i + 1])), [def]);

  // Drag and drop: grab a section or randomizer by its block, drop it on another element.
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hint, setHint] = useState<{ key: string; before: boolean; into?: boolean } | null>(null);
  const clearDrag = () => {
    setDrag(null);
    setHint(null);
  };
  const hintClass = (key: string) => (hint?.key === key ? (hint.into ? ' drop-into' : hint.before ? ' drop-above' : ' drop-below') : '');
  const dragSource = (d: Drag) => ({
    draggable: canEdit,
    onDragStart: (e: DragEvent) => {
      e.stopPropagation(); // a section inside a randomizer must not start dragging the randomizer
      setDrag(d);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', d.kind === 'section' ? d.title : d.id);
    },
    onDragEnd: clearDrag,
  });
  /** Handlers that make an element a drop target when `accept` allows the dragged thing. */
  const dropTarget = (key: string, accept: (d: Drag) => boolean, drop: (d: Drag, before: boolean) => void, into: (d: Drag) => boolean = () => false, swallow: (d: Drag) => boolean = () => false) => ({
    onDragOver: (e: DragEvent) => {
      // A drag this element refuses but owns (a question on itself) must not reach the section around it.
      if (canEdit && drag && !accept(drag) && swallow(drag)) e.stopPropagation();
      if (!canEdit || !drag || !accept(drag)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const before = !into(drag) && e.clientY < rect.top + rect.height / 2;
      const inside = into(drag);
      setHint((h) => (h?.key === key && h.before === before && !!h.into === inside ? h : { key, before, into: inside }));
    },
    onDrop: (e: DragEvent) => {
      if (canEdit && drag && !accept(drag) && swallow(drag)) e.stopPropagation();
      if (!canEdit || !drag || !accept(drag)) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const before = e.clientY < rect.top + rect.height / 2;
      const d = drag;
      clearDrag();
      drop(d, before);
    },
  });
  const allKeys = els.flatMap((e) => (e.kind === 'section' ? [blockKey(e.section)] : e.sections.map(blockKey)));
  const allOpen = allKeys.length > 0 && allKeys.every((k) => open.has(k));
  const toggle = (key: string) =>
    setOpen((o) => {
      const next = new Set(o);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  function create(kind: 'section' | 'randomizer', after?: string) {
    if (kind === 'section') return setNaming({ kind: 'new', after });
    const r = addRandomizer(def, after);
    onChange(() => r.def);
  }

  const addMenu = (after: string | undefined, label: ReactNode, className = 'flow-act add', ariaLabel?: string) => (
    <button type="button" className={className} aria-label={ariaLabel} title={ariaLabel} aria-expanded={adding?.where === 'below' && adding.after === after} onClick={() => setAdding({ where: 'below', after })}>
      {label}
    </button>
  );
  const addPanel = (at: AddAt) => (
    <AddPanel
      def={def}
      at={at}
      onClose={() => setAdding(null)}
      onSection={() => {
        setAdding(null);
        if (at.where === 'randomizer') setNaming({ kind: 'in-randomizer', randomizerId: at.randomizerId });
        else create('section', at.after);
      }}
      onExisting={(title) => {
        setAdding(null);
        if (at.where === 'randomizer') onChange((d) => moveSectionIntoRandomizer(d, title, at.randomizerId, null, 'after'));
        else if (at.after) onChange((d) => moveSectionTo(d, title, at.after!, 'after'));
      }}
      onRandomizer={() => {
        setAdding(null);
        create('randomizer', at.where === 'randomizer' ? undefined : at.after);
      }}
      onBranch={(end) => {
        setAdding(null);
        setBranching({ after: at.where === 'randomizer' ? def.settings.randomizers?.find((r) => r.id === at.randomizerId)?.sections.at(-1) : at.after, end });
      }}
    />
  );
  // Branches leave from the questions of a section; they are shown under it.
  const branches = branchesOf(def);
  // The same rule written twice (an old single jump and an answer route) shows once.
  const sectionBranches = (s: FlowSection) => {
    const ids = new Set(s.questions.map((q) => q.id));
    const seen = new Set<string>();
    return branches.filter((b) => {
      if (!ids.has(b.source.id)) return false;
      const sig = JSON.stringify([b.source.id, b.rule.operator, b.rule.value ?? null, b.rule.targetQuestionId, b.rule.targetEndingId ?? null]);
      if (seen.has(sig)) return false;
      seen.add(sig);
      return true;
    });
  };
  const branchCount = (s: FlowSection) => sectionBranches(s).length;
  const branchCards = (s: FlowSection) => {
    const list = sectionBranches(s);
    if (!list.length) return null;
    return (
      <div className="flow-branches">
        {list.map((b) => (
          <BranchCard key={`${b.source.id}:${b.index}`} def={def} branch={b} number={number} canEdit={canEdit} onRemove={() => onChange((d) => removeBranch(d, b.source.id, b.index))} />
        ))}
      </div>
    );
  };

  const question = (q: Question) => {
    const Icon = TYPE_ICONS[q.type];
    const n = number.get(q.id)!;
    const ref = (id: string) => t('flow.q_ref', { n: number.get(id) ?? 0 });
    const vi = q.visibleIf;
    const leads = logicFrom(def, q.id);
    const src = loopSource(def, q);
    return (
      <li
        key={q.id}
        className={`flow-qi${drag?.kind === 'question' && drag.id === q.id ? ' dragging' : ''}${hintClass(`q:${q.id}`)}`}
        {...dragSource({ kind: 'question', id: q.id })}
        {...dropTarget(
          `q:${q.id}`,
          (d) => d.kind === 'question' && d.id !== q.id,
          (d, before) => d.kind === 'question' && onChange((def) => moveQuestionTo(def, d.id, q.id, before ? 'before' : 'after')),
          undefined,
          (d) => d.kind === 'question',
        )}
      >
        <button type="button" className={`flow-q${q.id === selectedId ? ' active' : ''}`} onClick={() => onSelect(q.id)} aria-current={q.id === selectedId || undefined}>
          {canEdit && <GripVertical className="flow-q-grip" aria-hidden />}
          <span className="n">{n}</span>
          <span className="grow">
            <span className={`t${q.title ? '' : ' empty'}`}>{q.title || t('common.untitled_question')}</span>
            {vi && vi.conditions.length > 0 && (
              <span className="flow-line">
                <Eye />
                <span>
                  {t('flow.shown_if')}{' '}
                  {vi.conditions.map((c, k) => (
                    <span key={k}>
                      {k > 0 && ` ${t(vi.match === 'any' ? 'flow.or' : 'flow.and')} `}
                      <strong>{ref(c.questionId)}</strong> {conditionPhrase(def.questions.find((x) => x.id === c.questionId), c.operator, c.value, t, locale)}
                    </span>
                  ))}
                </span>
              </span>
            )}
            {leads.map((l, i) => (
              <span key={i} className="flow-line lead">
                {l.kind === 'shows' ? <CornerDownRight /> : <ListPlus />}
                <span>
                  {l.kind === 'shows'
                    ? t('flow.leads', { cond: l.conditions.map((c) => conditionPhrase(q, c.operator, c.value, t, locale)).join(` ${t(l.match === 'any' ? 'flow.or' : 'flow.and')} `) })
                    : t('flow.carries_to')}{' '}
                  <strong>{ref(l.target.id)}</strong>
                </span>
              </span>
            ))}
            {jumpsOf(q).map((j, k) =>
              j.targetEndingId ? (
                <span key={`j${k}`} className="flow-line lead">
                  <Flag />
                  <span>
                    {t('flow.ends_if', { cond: conditionPhrase(q, j.operator, j.value, t, locale) })} <strong>{def.endings?.find((e) => e.id === j.targetEndingId)?.name ?? '?'}</strong>
                  </span>
                </span>
              ) : j.targetQuestionId ? (
                <span key={`j${k}`} className="flow-line lead">
                  <CornerDownRight />
                  <span>
                    {t('flow.jump_if', { cond: conditionPhrase(q, j.operator, j.value, t, locale) })} <strong>{ref(j.targetQuestionId)}</strong>
                  </span>
                </span>
              ) : null,
            )}
            {q.carryForward && (
              <span className="flow-line">
                <ListPlus />
                <span>{t(q.carryForward.mode === 'selected' ? 'flow.carries_selected' : 'flow.carries_not_selected', { q: ref(q.carryForward.questionId) })}</span>
              </span>
            )}
            {src && (
              <span className="flow-line">
                <Repeat />
                <span>{t('flow.loops', { q: ref(src) })}</span>
              </span>
            )}
            {q.shuffleWithLogic && (
              <span className="flow-line">
                <Shuffle />
                <span>{t('flow.shuffled_with_logic')}</span>
              </span>
            )}
          </span>
          <span className="flow-type">
            <Icon />
            <span className="muted">{questionTypeLabel(q.type, locale)}</span>
          </span>
        </button>
      </li>
    );
  };

  const block = (s: FlowSection, ctx: { randomizer?: Randomizer; first?: boolean; last?: boolean }) => {
    const key = blockKey(s);
    const isOpen = open.has(key);
    const inGroup = !!ctx.randomizer;
    const movable = canEdit && !!s.title;
    const group = ctx.randomizer;
    // Inside a randomizer a section can be dropped on its siblings to reorder them.
    const nestedDrop = group
      ? dropTarget(
          `sec:${s.title}`,
          (d) => d.kind === 'question' || (d.kind === 'section' && d.title !== s.title),
          (d, before) => {
            if (d.kind === 'question') onChange((def) => moveQuestionToSection(def, d.id, s.title));
            else if (d.kind === 'section') {
              const pos = before ? 'before' : 'after';
              // A section from outside joins the randomizer next to this one.
              onChange((def) => (group.sections.includes(d.title) ? moveSectionTo(def, d.title, s.title, pos) : moveSectionIntoRandomizer(def, d.title, group.id, s.title, pos)));
            }
          },
          (d) => d.kind === 'question',
        )
      : {};
    return (
      <div
        key={key}
        className={`flow-block${inGroup ? ' nested' : ''}${drag?.kind === 'section' && drag.title === s.title ? ' dragging' : ''}${group ? hintClass(`sec:${s.title}`) : ''}`}
        {...(movable ? dragSource({ kind: 'section', title: s.title }) : {})}
        {...nestedDrop}
      >
        {movable && (
          <span className="flow-grip" title={t('builder.drag_section')} aria-hidden>
            <GripVertical />
          </span>
        )}
        <div className="flow-icon">
          <Box />
        </div>
        <div className="flow-main">
          <div className="flow-head">
            <button type="button" className="flow-toggle" onClick={() => toggle(key)} aria-expanded={isOpen} aria-label={t('flow.toggle_questions')} title={t('flow.toggle_questions')}>
              {isOpen ? <ChevronDown /> : <ChevronRight />}
            </button>
            <div className="flow-title">
              <strong className={s.title ? '' : 'untitled'}>{s.title || t('flow.no_section')}</strong>
              <span className="flow-chip">{t('flow.n_questions', { count: s.questions.length }).replace(/[()]/g, '')}</span>
              {branchCount(s) > 0 && (
                <span className="flow-chip branch">
                  <GitBranch /> {branchCount(s)}
                </span>
              )}
            </div>
            {canEdit && (
              <div className="flow-tools">
                {!inGroup && addMenu(s.title || undefined, <Plus />, 'btn ghost sm icon', t('flow.add_below'))}
                <Menu align="right" trigger={(toggleMenu) => <button type="button" className="btn ghost sm icon" onClick={toggleMenu} aria-label={t('flow.more_actions')} title={t('flow.more_actions')}><MoreHorizontal /></button>}>
                  {(close) =>
                    s.title ? (
                      <>
                        <button type="button" disabled={ctx.first} onClick={() => { close(); onChange((d) => moveSection(d, s.title, -1)); }}>{t('flow.move_up')}</button>
                        <button type="button" disabled={ctx.last} onClick={() => { close(); onChange((d) => moveSection(d, s.title, 1)); }}>{t('flow.move_down')}</button>
                        <button type="button" onClick={() => { close(); onChange((d) => duplicateSection(d, s.title).def); }}>{t('flow.duplicate')}</button>
                        <button type="button" onClick={() => { close(); setNaming({ kind: 'rename', title: s.title }); }}>{t('flow.rename')}</button>
                        {ctx.randomizer && (ctx.first || ctx.last) && ctx.randomizer.sections.length > 1 && (
                          <button type="button" onClick={() => { close(); onChange((d) => releaseSection(d, ctx.randomizer!.id, s.title)); }}>{t('flow.rz_take_out')}</button>
                        )}
                        <button type="button" className="danger" onClick={() => { close(); if (confirmAction(t('flow.confirm_delete_section', { title: s.title, count: s.questions.length }))) onChange((d) => deleteSection(d, s.title)); }}>{t('flow.delete')}</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => { close(); setNaming({ kind: 'name-untitled', firstId: s.questions[0]!.id }); }}>{t('flow.name_untitled')}</button>
                    )
                  }
                </Menu>
              </div>
            )}
          </div>
          {isOpen && <ul className="flow-questions">{s.questions.map(question)}</ul>}
          {branchCards(s)}
        </div>
      </div>
    );
  };

  const randomizer = (r: Randomizer, sections: FlowSection[], at: number) => {
    const above = neighbourSection(def, r.id, 'above');
    const below = neighbourSection(def, r.id, 'below');
    const lastTitle = sections[sections.length - 1]?.title;
    // The gaps and padding between a randomizer's sections belong to the nearest section, so a drop that
    // lands between two blocks still reorders them (browsers only offer a drop where something accepts it).
    const nearest = (e: DragEvent): { title: string; before: boolean } | null => {
      const blocks = [...e.currentTarget.querySelectorAll<HTMLElement>(':scope > .flow-block')];
      let best: { title: string; before: boolean; dist: number } | null = null;
      blocks.forEach((el, i) => {
        const title = sections[i]?.title;
        if (!title) return;
        const rect = el.getBoundingClientRect();
        const dist = e.clientY < rect.top ? rect.top - e.clientY : e.clientY > rect.bottom ? e.clientY - rect.bottom : 0;
        if (!best || dist < best.dist) best = { title, before: e.clientY < rect.top + rect.height / 2, dist };
      });
      return best;
    };
    const memberDrag = (d: Drag | null): d is Extract<Drag, { kind: 'section' }> => !!d && d.kind === 'section';
    const gapDrop = canEdit
      ? {
          onDragOver: (e: DragEvent) => {
            if (!memberDrag(drag)) return;
            const near = nearest(e);
            if (!near || near.title === drag.title) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            const key = `sec:${near.title}`;
            setHint((h) => (h?.key === key && h.before === near.before && !h.into ? h : { key, before: near.before }));
          },
          onDrop: (e: DragEvent) => {
            if (!memberDrag(drag)) return;
            const near = nearest(e);
            if (!near || near.title === drag.title) return;
            e.preventDefault();
            e.stopPropagation();
            const d = drag;
            clearDrag();
            const pos = near.before ? 'before' : 'after';
            onChange((def) => (r.sections.includes(d.title) ? moveSectionTo(def, d.title, near.title, pos) : moveSectionIntoRandomizer(def, d.title, r.id, near.title, pos)));
          },
        }
      : {};
    return (
      <div
        key={`${r.id}:${at}`}
        className={`flow-randomizer${drag?.kind === 'randomizer' && drag.id === r.id ? ' dragging' : ''}`}
        {...(canEdit ? dragSource({ kind: 'randomizer', id: r.id }) : {})}
      >
        <div className="flow-block">
          {canEdit && (
            <span className="flow-grip" title={t('flow.drag_randomizer')} aria-hidden>
              <GripVertical />
            </span>
          )}
          <div className="flow-icon rz">
            <Shuffle />
          </div>
          <div className="flow-main">
            <div className="flow-head">
              <div className="flow-title">
                <strong>{t('flow.randomizer')}</strong>
                <div className="flow-count">
                  <span>{t('flow.rz_present')}</span>
                  <button type="button" className="icon-btn" disabled={!canEdit || r.present <= 1} onClick={() => onChange((d) => setRandomizerCount(d, r.id, r.present - 1))} aria-label={t('flow.rz_fewer')}>
                    <Minus />
                  </button>
                  <output className="flow-count-n" aria-live="polite">{r.present}</output>
                  <button type="button" className="icon-btn" disabled={!canEdit || r.present >= r.sections.length} onClick={() => onChange((d) => setRandomizerCount(d, r.id, r.present + 1))} aria-label={t('flow.rz_more')}>
                    <Plus />
                  </button>
                  <span>{t('flow.rz_of', { count: r.sections.length })}</span>
                </div>
              </div>
              {canEdit && (
                <div className="flow-tools">
                  {addMenu(lastTitle, <Plus />, 'btn ghost sm icon', t('flow.add_below'))}
                  <Menu align="right" trigger={(toggleMenu) => <button type="button" className="btn ghost sm icon" onClick={toggleMenu} aria-label={t('flow.more_actions')} title={t('flow.more_actions')}><MoreHorizontal /></button>}>
                    {(close) => (
                      <>
                        {above && <button type="button" onClick={() => { close(); onChange((d) => absorbSection(d, r.id, 'above')); }}>{t('flow.rz_include_above', { title: above })}</button>}
                        {below && <button type="button" onClick={() => { close(); onChange((d) => absorbSection(d, r.id, 'below')); }}>{t('flow.rz_include_below', { title: below })}</button>}
                        <button type="button" onClick={() => { close(); onChange((d) => moveRandomizer(d, r.id, -1)); }}>{t('flow.move_up')}</button>
                        <button type="button" onClick={() => { close(); onChange((d) => moveRandomizer(d, r.id, 1)); }}>{t('flow.move_down')}</button>
                        <button type="button" className="danger" onClick={() => { close(); if (confirmAction(t('flow.rz_confirm_remove'))) onChange((d) => removeRandomizer(d, r.id)); }}>{t('flow.rz_remove')}</button>
                      </>
                    )}
                  </Menu>
                </div>
              )}
            </div>
            {sections.length > 0 && (
              <div className="flow-limits">
                <label className="row gap-8 small">
                  <input type="checkbox" checked={!!r.evenly} disabled={!canEdit} onChange={(e) => onChange((d) => setRandomizerLimits(d, r.id, { evenly: e.target.checked, limits: r.limits, resetAt: r.resetAt }))} />
                  {t('flow.rz_evenly')}
                </label>
                <button type="button" className="flow-act" onClick={() => setLimiting({ randomizerId: r.id })}>
                  {t('flow.rz_edit_counts')}
                  {r.limits && Object.keys(r.limits).length > 0 ? ` (${Object.keys(r.limits).length})` : ''}
                </button>
              </div>
            )}
            <p className="flow-hint">{sections.length ? t('flow.rz_hint', { n: r.present }) : t('flow.rz_empty')}</p>
          </div>
        </div>
        <div className="flow-children" {...gapDrop}>
          {sections.map((s, i) => block(s, { randomizer: r, first: i === 0, last: i === sections.length - 1 }))}
          {canEdit &&
            (adding?.where === 'randomizer' && adding.randomizerId === r.id ? (
              addPanel(adding)
            ) : (
              <button type="button" className="flow-add" onClick={() => setAdding({ where: 'randomizer', randomizerId: r.id })}>
                <Plus /> {t('flow.add_element')}
              </button>
            ))}
        </div>
      </div>
    );
  };

  if (def.questions.length === 0 && !canEdit) return <p className="small muted">{t('flow.empty')}</p>;

  return (
    <div className="flow-editor">
      <div className="flow-toolbar">
        <p className="small muted grow">{t('flow.help')}</p>
        {allKeys.length > 0 && (
          <button type="button" className="btn sm" onClick={() => setOpen(allOpen ? new Set() : new Set(allKeys))}>
            {allOpen ? <ChevronsDownUp /> : <ChevronsUpDown />} {t(allOpen ? 'builder.collapse_all' : 'builder.expand_all')}
          </button>
        )}
        {openUrl && (
          <a className="btn sm" href={openUrl} target="_blank" rel="noreferrer">
            <ExternalLink /> {t('flow.open_new_tab')}
          </a>
        )}
      </div>
      {rzIssues.length > 0 && (
        <div className="banner warn flow-issues" role="alert">
          <AlertTriangle />
          <div>
            {rzIssues.map((i, n) => (
              <div key={n}>{i.message}</div>
            ))}
          </div>
        </div>
      )}
      <div className="flow-layout">
      <div className="flow-canvas">
        {els.map((e, i) => {
          // Dropping next to a randomizer's section puts the dragged one next to the whole group.
          const target = e.kind === 'section' ? e.section.title : (e.sections[0]?.title ?? '');
          const own = e.kind === 'randomizer' ? e.randomizer : null;
          const key = `el:${i}`;
          const drop = dropTarget(
            key,
            // A question dropped on a section joins it; on a randomizer's frame it does nothing (its sections take drops themselves).
            (d) => (own && !own.sections.length ? d.kind === 'section' : !!target) && (d.kind === 'question' ? !own : d.kind === 'section' ? !(own && own.sections.includes(d.title)) && d.title !== target : !(own && own.id === d.id)),
            (d, before) =>
              onChange((def) =>
                d.kind === 'question'
                  ? moveQuestionToSection(def, d.id, target)
                  : d.kind === 'section'
                    ? own && !own.sections.length
                      ? moveSectionIntoRandomizer(def, d.title, own.id, null, 'after')
                      : moveSectionTo(def, d.title, target, before ? 'before' : 'after')
                    : moveRandomizerTo(def, d.id, target, before ? 'before' : 'after'),
              ),
            (d) => d.kind === 'question',
          );
          return (
            <div key={e.kind === 'section' ? blockKey(e.section) : `${e.randomizer.id}:${i}`} className={`flow-el${hintClass(key)}`} {...drop}>
              {e.kind === 'section' ? block(e.section, { first: i === 0, last: i === els.length - 1 }) : randomizer(e.randomizer, e.sections, i)}
              {adding?.where === 'below' && adding.after !== undefined && adding.after === lastTitleOf(e) && addPanel(adding)}
            </div>
          );
        })}
        {canEdit && (
          <div className="flow-el">
            {els.length === 0 ? (
              <button type="button" className="flow-add" onClick={() => setNaming({ kind: 'new' })}>
                <Plus /> {t('flow.add_element')}
              </button>
            ) : adding?.where === 'end' ? (
              addPanel(adding)
            ) : (
              <button type="button" className="flow-add" onClick={() => setAdding({ where: 'end', after: lastTitleOf(els.at(-1)!) })}>
                <Plus /> {t('flow.add_element')}
              </button>
            )}
          </div>
        )}
      </div>
      <FlowOverview def={def} branches={branches.length} canEdit={canEdit} onChange={onChange} />
      </div>

      {naming && (
        <NameDialog
          def={def}
          naming={naming}
          onClose={() => setNaming(null)}
          onSave={(title) => {
            const n = naming;
            if (n.kind === 'new') {
              const r = newSection(def, title, n.after);
              onChange(() => r.def);
              setOpen((o) => new Set(o).add(r.title));
            } else if (n.kind === 'rename') onChange((d) => renameSectionTitle(d, n.title, title));
            else if (n.kind === 'name-untitled') onChange((d) => startSection(d, n.firstId, title));
            else {
              const r = addSectionToRandomizer(def, n.randomizerId, title);
              if (r) {
                onChange(() => r.def);
                setOpen((o) => new Set(o).add(r.title));
              }
            }
            setNaming(null);
          }}
        />
      )}
      {branching && (
        <BranchDialog
          def={def}
          after={branching.after}
          end={branching.end}
          onClose={() => setBranching(null)}
          onSave={(fn) => {
            onChange(fn);
            setBranching(null);
          }}
        />
      )}
      {limiting && def.settings.randomizers?.find((r) => r.id === limiting.randomizerId) && (
        <LimitsDialog
          randomizer={def.settings.randomizers.find((r) => r.id === limiting.randomizerId)!}
          surveyId={surveyId}
          canEdit={canEdit}
          onClose={() => setLimiting(null)}
          onSave={(patch) => {
            onChange((d) => setRandomizerLimits(d, limiting.randomizerId, patch));
            setLimiting(null);
          }}
        />
      )}
    </div>
  );
}

/** How many respondents each section of a randomizer may be given, with how many it has been given so far. */
function LimitsDialog({ randomizer: r, surveyId, canEdit, onClose, onSave }: { randomizer: Randomizer; surveyId?: string; canEdit: boolean; onClose: () => void; onSave: (patch: Pick<Randomizer, 'evenly' | 'limits' | 'resetAt'>) => void }) {
  const { t } = useI18n();
  const [evenly, setEvenly] = useState(!!r.evenly);
  const [limits, setLimits] = useState<Record<string, string>>(() => Object.fromEntries(r.sections.map((s) => [s, r.limits?.[s] ? String(r.limits[s]) : ''])));
  const [resetAt, setResetAt] = useState<string | undefined>(r.resetAt);
  const [counts, setCounts] = useState<Record<string, number> | null>(surveyId ? null : {});
  const [failed, setFailed] = useState(false);
  const [zeroed, setZeroed] = useState(false);
  useEffect(() => {
    if (!surveyId) return;
    let live = true;
    api
      .flowCounts(surveyId)
      .then((res) => live && setCounts(res.counts[r.id] ?? {}))
      .catch(() => live && (setCounts({}), setFailed(true)));
    return () => {
      live = false;
    };
  }, [surveyId, r.id]);
  const shown = (title: string) => (zeroed ? 0 : (counts?.[title] ?? 0));
  const total = r.sections.reduce((n, title) => n + shown(title), 0);
  const hasLimits = Object.values(limits).some((v) => v.trim() !== '');
  const save = () => {
    const out: Record<string, number> = {};
    for (const [title, v] of Object.entries(limits)) {
      const n = Math.floor(Number(v));
      if (v.trim() !== '' && Number.isFinite(n) && n >= 1) out[title] = n;
    }
    onSave({ evenly, limits: out, resetAt });
  };
  return (
    <Modal
      title={t('flow.rz_counts_title')}
      width={560}
      onClose={onClose}
      footer={
        <>
          {canEdit && (
            <div className="row gap-4 grow">
              <button type="button" className="btn ghost sm" disabled={zeroed} onClick={() => confirmAction(t('flow.rz_counts_reset_confirm')) && (setResetAt(new Date().toISOString()), setZeroed(true))}>
                <RotateCcw /> {t('flow.rz_counts_reset')}
              </button>
              <button type="button" className="btn ghost sm" disabled={!hasLimits} onClick={() => setLimits(Object.fromEntries(r.sections.map((s) => [s, ''])))}>
                {t('flow.rz_counts_clear')}
              </button>
            </div>
          )}
          <button type="button" className="btn" onClick={onClose}>
            {canEdit ? t('common.cancel') : t('common.close')}
          </button>
          {canEdit && (
            <button type="button" className="btn primary" onClick={save}>
              {t('common.save')}
            </button>
          )}
        </>
      }
    >
      <div className="stack gap-16 rz-counts">
        <p className="small muted">{t('flow.rz_counts_help')}</p>
        <label className={`rz-evenly${evenly ? ' on' : ''}`}>
          <input type="checkbox" checked={evenly} disabled={!canEdit} onChange={(e) => setEvenly(e.target.checked)} />
          <span>
            <strong>{t('flow.rz_evenly')}</strong>
            <span className="small muted">{t('flow.rz_evenly_hint')}</span>
          </span>
        </label>
        <div className="stack gap-8">
          <div className="row rz-limits-head">
            <span className="small muted grow">{t('flow.rz_counts_section')}</span>
            <span className="small muted">{counts === null ? '…' : t('flow.rz_counts_total', { n: total })}</span>
          </div>
          <ul className="rz-limits">
            {r.sections.map((title) => {
              const given = shown(title);
              const cap = Math.floor(Number(limits[title]));
              const capped = (limits[title] ?? '').trim() !== '' && Number.isFinite(cap) && cap >= 1;
              const full = capped && given >= cap;
              return (
                <li key={title} className={`rz-limit${full ? ' full' : ''}`}>
                  <div className="rz-limit-name">
                    <strong title={title}>{title}</strong>
                    <span className="rz-limit-given">
                      {counts === null ? '…' : given}
                      {capped ? ` / ${cap}` : ''} {t('flow.rz_counts_given_short')}
                      {full && <span className="pill orange">{t('flow.rz_counts_full')}</span>}
                    </span>
                    {capped && (
                      <span className="rz-limit-bar" aria-hidden>
                        <span style={{ width: `${Math.min(100, (given / cap) * 100)}%` }} />
                      </span>
                    )}
                  </div>
                  <label className="rz-limit-field">
                    <span className="small muted">{t('flow.rz_counts_limit')}</span>
                    <input
                      className="input"
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={limits[title] ?? ''}
                      placeholder={t('flow.rz_counts_none')}
                      disabled={!canEdit}
                      aria-label={t('flow.rz_counts_limit_for', { title })}
                      onChange={(e) => setLimits((l) => ({ ...l, [title]: e.target.value }))}
                    />
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        {failed && <p className="small muted">{t('flow.rz_counts_failed')}</p>}
        {zeroed && <p className="hint">{t('flow.rz_counts_reset_pending')}</p>}
        <div className="rz-note">
          <Info />
          <span>{t('flow.rz_counts_note')}</span>
        </div>
      </div>
    </Modal>
  );
}

/** Beside the flow: what it contains, what each colour means, and how to work with it. */
function FlowOverview({ def, branches, canEdit, onChange }: { def: Definition; branches: number; canEdit: boolean; onChange: (fn: (d: Definition) => Definition) => void }) {
  const { t } = useI18n();
  const sections = sectionTitlesOf(def).length;
  const randomizers = (def.settings.randomizers ?? []).length;
  const endings = (def.endings ?? []).length;
  const stats: [string, number, string][] = [
    [t('flow.ov_sections'), sections, 'section'],
    [t('flow.ov_questions'), def.questions.length, 'question'],
    [t('flow.ov_randomizers'), randomizers, 'rz'],
    [t('flow.ov_branches'), branches, 'branch'],
    [t('flow.ov_endings'), endings, 'end'],
  ];
  return (
    <aside className="flow-overview" aria-label={t('flow.ov_title')}>
      <h3>{t('flow.ov_title')}</h3>
      <dl className="flow-stats">
        {stats.map(([label, n, kind]) => (
          <div key={kind} className={`flow-stat ${kind}`}>
            <dt>{label}</dt>
            <dd>{n}</dd>
          </div>
        ))}
      </dl>
      <h3>{t('flow.ov_moving')}</h3>
      <label className="flow-back">
        <input
          type="checkbox"
          checked={def.settings.allowBack}
          disabled={!canEdit}
          onChange={(e) => onChange((d) => ({ ...d, settings: { ...d.settings, allowBack: e.target.checked } }))}
        />
        <span>
          <strong>{t('builder.allow_back')}</strong>
          <span className="small muted">{t(def.settings.allowBack ? 'builder.allow_back_on' : 'builder.allow_back_off')}</span>
        </span>
      </label>
      <h3>{t('flow.ov_how')}</h3>
      <ol className="flow-tips">
        <li>{t('flow.ov_tip_order')}</li>
        <li>{t('flow.ov_tip_add')}</li>
        <li>{t('flow.ov_tip_branch')}</li>
        <li>{t('flow.ov_tip_rz')}</li>
      </ol>
    </aside>
  );
}

/** "What do you want to add?": the kinds of flow element, inline where they will go. */
function AddPanel({ def, at, onClose, onSection, onExisting, onRandomizer, onBranch }: { def: Definition; at: AddAt; onClose: () => void; onSection: () => void; onExisting: (title: string) => void; onRandomizer: () => void; onBranch: (end: boolean) => void }) {
  const { t } = useI18n();
  const [picking, setPicking] = useState(false);
  const here = at.where === 'randomizer' ? (def.settings.randomizers?.find((r) => r.id === at.randomizerId)?.sections ?? []) : [];
  const existing = sectionTitlesOf(def).filter((title) => title !== (at.where === 'randomizer' ? undefined : at.after) && !here.includes(title));
  const [choice, setChoice] = useState(existing[0] ?? '');
  const inRandomizer = at.where === 'randomizer';
  return (
    <div className="flow-addpanel" role="group" aria-label={t('flow.add_what')}>
      <div className="row gap-8">
        <strong className="small">{t('flow.add_what')}</strong>
        <button type="button" className="flow-act" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
      {picking ? (
        <div className="row gap-8 wrap">
          <select className="select" style={{ width: 'auto', minWidth: 200 }} value={choice} aria-label={t('flow.add_existing')} onChange={(e) => setChoice(e.target.value)}>
            {existing.map((title) => (
              <option key={title} value={title}>
                {title}
              </option>
            ))}
          </select>
          <button type="button" className="btn sm primary" disabled={!choice} onClick={() => onExisting(choice)}>
            {t('flow.add_existing_here')}
          </button>
          <button type="button" className="btn sm ghost" onClick={() => setPicking(false)}>
            {t('common.back')}
          </button>
        </div>
      ) : (
        <div className="flow-addpanel-kinds">
          <button type="button" className="flow-kind" onClick={onSection}>
            <Box /> {t('flow.add_section')}
          </button>
          <button type="button" className="flow-kind" disabled={!existing.length} onClick={() => setPicking(true)}>
            <Boxes /> {t('flow.add_existing')}
          </button>
          {!inRandomizer && (
            <button type="button" className="flow-kind branch" disabled={!def.questions.length} onClick={() => onBranch(false)}>
              <GitBranch /> {t('flow.add_branch')}
            </button>
          )}
          {!inRandomizer && (
            <button type="button" className="flow-kind rz" onClick={onRandomizer}>
              <Shuffle /> {t('flow.add_randomizer')}
            </button>
          )}
          {!inRandomizer && (
            <button type="button" className="flow-kind end" disabled={!def.questions.length} onClick={() => onBranch(true)}>
              <OctagonX /> {t('flow.add_end')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function sectionTitlesOf(def: Definition): string[] {
  return [...new Set(def.questions.map((q) => q.sectionTitle?.trim() ?? '').filter(Boolean))];
}

/** A branch shown in the flow under the section its question is in. */
function BranchCard({ def, branch: b, number, canEdit, onRemove }: { def: Definition; branch: Branch; number: Map<string, number>; canEdit: boolean; onRemove: () => void }) {
  const { t, locale } = useI18n();
  const q = b.source;
  const target = b.rule.targetQuestionId ? def.questions.find((x) => x.id === b.rule.targetQuestionId) : undefined;
  const idx = target ? def.questions.indexOf(target) : -1;
  // A jump to a section's first question reads as going to that section.
  const startsSection = !!target?.sectionTitle?.trim() && def.questions[idx - 1]?.sectionTitle?.trim() !== target.sectionTitle!.trim();
  const ending = b.rule.targetEndingId ? def.endings?.find((e) => e.id === b.rule.targetEndingId) : undefined;
  return (
    <div className={`flow-branch${ending ? ' end' : ''}`}>
      <span className="flow-branch-icon">{ending ? <OctagonX /> : <GitBranch />}</span>
      <span className="grow">
        <strong>{t('flow.branch')}</strong> {t('flow.branch_if', { q: t('flow.q_ref', { n: number.get(q.id) ?? 0 }), cond: conditionPhrase(q, b.rule.operator, b.rule.value, t, locale) })}{' '}
        <strong>
          {ending
            ? t('flow.branch_end', { name: ending.name })
            : target
              ? startsSection
                ? t('flow.branch_section', { title: target.sectionTitle!.trim(), q: t('flow.q_ref', { n: idx + 1 }) })
                : t('flow.q_ref', { n: idx + 1 })
              : '?'}
        </strong>
      </span>
      {canEdit && (
        <button type="button" className="btn ghost sm icon" aria-label={t('flow.branch_remove')} onClick={() => confirmAction(t('flow.branch_confirm_remove')) && onRemove()}>
          <Trash2 />
        </button>
      )}
    </div>
  );
}

/** If question A's answer matches, go to a later question, a later section, or end the survey. */
function BranchDialog({ def, after, end, onClose, onSave }: { def: Definition; after?: string; end?: boolean; onClose: () => void; onSave: (fn: (d: Definition) => Definition) => void }) {
  const { t, locale } = useI18n();
  const sources = def.questions.filter((q) => q.type !== 'descriptive');
  // The section the branch is added under suggests its last question as the one to read.
  const suggested = after ? [...sources].reverse().find((q) => q.sectionTitle?.trim() === after) : undefined;
  const [sourceId, setSourceId] = useState((suggested ?? sources[sources.length - 1])?.id ?? '');
  const src = def.questions.find((q) => q.id === sourceId);
  const ops = src ? operatorsFor(src) : [];
  const [operator, setOperator] = useState<Operator>(ops[0] ?? 'answered');
  const [value, setValue] = useState<Condition['value']>(src ? defaultValue(src) : '');
  const idx = def.questions.findIndex((q) => q.id === sourceId);
  const later = def.questions.slice(idx + 1);
  const laterSections = later.filter((q, i) => q.sectionTitle?.trim() && (i === 0 ? def.questions[idx]?.sectionTitle?.trim() : later[i - 1]!.sectionTitle?.trim()) !== q.sectionTitle.trim());
  const endings = def.endings ?? [];
  const [target, setTarget] = useState(end ? (endings[0] ? `end:${endings[0].id}` : 'end:new') : (laterSections[0]?.id ?? later[0]?.id ?? 'end:new'));
  const pickSource = (id: string) => {
    const next = def.questions.find((q) => q.id === id);
    setSourceId(id);
    if (next) {
      setOperator(operatorsFor(next)[0] ?? 'answered');
      setValue(defaultValue(next));
    }
    const at = def.questions.findIndex((q) => q.id === id);
    if (!target.startsWith('end:') && def.questions.findIndex((q) => q.id === target) <= at) setTarget(def.questions[at + 1]?.id ?? 'end:new');
  };
  const needsValue = !['answered', 'not_answered'].includes(operator);
  const save = () => {
    if (!src) return;
    onSave((d) => {
      let next = d;
      let rule: JumpIf;
      if (target === 'end:new') {
        const made = addEnding(next, t('flow.end_default_name'), t('flow.end_default_message'));
        next = made.def;
        rule = { operator, ...(needsValue ? { value } : {}), targetQuestionId: '', targetEndingId: made.id };
      } else if (target.startsWith('end:')) rule = { operator, ...(needsValue ? { value } : {}), targetQuestionId: '', targetEndingId: target.slice(4) };
      else rule = { operator, ...(needsValue ? { value } : {}), targetQuestionId: target };
      return addBranch(next, src.id, rule);
    });
  };
  return (
    <Modal
      title={t(end ? 'flow.end_title' : 'flow.branch_title')}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn primary" disabled={!src || (needsValue && (value === undefined || value === ''))} onClick={save}>
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="stack gap-12">
        <p className="small muted">{t(end ? 'flow.end_help' : 'flow.branch_help')}</p>
        <Field label={t('flow.branch_when')} htmlFor="branch-q">
          <select id="branch-q" className="select" value={sourceId} onChange={(e) => pickSource(e.target.value)}>
            {sources.map((q) => (
              <option key={q.id} value={q.id}>
                {def.questions.indexOf(q) + 1}. {q.title || t('common.untitled_question')}
              </option>
            ))}
          </select>
        </Field>
        {src && (
          <div className="row gap-8 wrap">
            <select className="select" style={{ width: 'auto' }} value={operator} aria-label={t('flow.branch_operator')} onChange={(e) => setOperator(e.target.value as Operator)}>
              {ops.map((op) => (
                <option key={op} value={op}>
                  {operatorLabel(op, locale)}
                </option>
              ))}
            </select>
            {needsValue && <ValueInput src={src} value={value} onChange={setValue} />}
          </div>
        )}
        <Field label={t('flow.branch_then')} htmlFor="branch-target">
          <select id="branch-target" className="select" value={target} onChange={(e) => setTarget(e.target.value)}>
            {laterSections.length > 0 && (
              <optgroup label={t('flow.branch_to_section')}>
                {laterSections.map((q) => (
                  <option key={`s:${q.id}`} value={q.id}>
                    {q.sectionTitle!.trim()}
                  </option>
                ))}
              </optgroup>
            )}
            {later.length > 0 && (
              <optgroup label={t('flow.branch_to_question')}>
                {later.map((q) => (
                  <option key={q.id} value={q.id}>
                    {def.questions.indexOf(q) + 1}. {q.title || t('common.untitled_question')}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label={t('flow.branch_to_end')}>
              {endings.map((e) => (
                <option key={e.id} value={`end:${e.id}`}>
                  {t('logic.end_with', { name: e.name || e.id })}
                </option>
              ))}
              <option value="end:new">{t('flow.end_new')}</option>
            </optgroup>
          </select>
        </Field>
        {isChoice(src?.type ?? 'short_text') && <p className="hint">{t('flow.branch_choice_hint')}</p>}
      </div>
    </Modal>
  );
}

function lastTitleOf(e: ReturnType<typeof flowElements>[number]): string | undefined {
  const s = e.kind === 'section' ? e.section : e.sections[e.sections.length - 1];
  return s?.title || undefined;
}

function NameDialog({ def, naming, onClose, onSave }: { def: Definition; naming: Naming; onClose: () => void; onSave: (title: string) => void }) {
  const { t } = useI18n();
  const renaming = naming.kind === 'rename';
  const [title, setTitle] = useState(renaming ? naming.title : '');
  const taken = def.questions.some((q) => q.sectionTitle?.trim() === title.trim()) && !(renaming && title.trim() === naming.title);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim() && !taken) onSave(title.trim());
  };
  // A new section may reuse a base name; the model makes it unique. Only renames and naming existing questions must not clash.
  const clash = taken && (renaming || naming.kind === 'name-untitled');
  return (
    <Modal
      title={t(renaming ? 'flow.rename_title' : 'flow.new_section_title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="flow-name" className="btn primary" disabled={!title.trim() || clash}>
            {t('common.save')}
          </button>
        </>
      }
    >
      <form id="flow-name" onSubmit={submit}>
        <Field label={t('builder.section_name')} htmlFor="flow-section-name" error={clash ? t('flow.name_taken') : undefined}>
          <input id="flow-section-name" className="input" autoFocus value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder={t('qe.section_placeholder')} />
        </Field>
      </form>
    </Modal>
  );
}
